import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { TagIcon } from "./TagIcon";
import { X, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTagAssignment } from "@/hooks/useTagAssignment";
import { getTagsByCustomerId } from "@/utils/customerTags";
import * as tagsApi from "@/services/tagsApi";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Tag { id: string; name: string; color: string; icon?: string; type: string; }

interface TagsDisplayProps {
  resourceId: string;
  resourceType: "device" | "profile" | "application" | "configuration" | "patch_policy" | "user";
  maxTags?: number; size?: "sm" | "default"; className?: string;
  prefetchedTags?: Tag[]; showRemoveButton?: boolean; onTagRemoved?: () => void;
  customerIds?: string[]; assignedTagIds?: string[]; useMockData?: boolean;
}

export const TagsDisplay = ({
  resourceId, resourceType, maxTags = 3, size = "default", className = "",
  prefetchedTags, showRemoveButton = false, onTagRemoved,
  customerIds, assignedTagIds, useMockData
}: TagsDisplayProps) => {
  const [tags, setTags] = useState<Tag[]>(prefetchedTags || []);
  const [isLoading, setIsLoading] = useState(!prefetchedTags);
  const [tagToRemove, setTagToRemove] = useState<{id: string, name: string} | null>(null);
  const { toast } = useToast();
  const { removeTag, isLoading: isRemoving } = useTagAssignment(resourceType);

  useEffect(() => {
    if (prefetchedTags) { setTags(prefetchedTags); setIsLoading(false); }
    else { fetchTags(); }
  }, [resourceId, prefetchedTags]);

  const fetchTags = async () => {
    try {
      if (useMockData || (customerIds && assignedTagIds)) { fetchMockTags(); return; }

      // Use the real API based on resource type
      let fetchedTags: any[] = [];
      try {
        if (resourceType === "device") fetchedTags = await tagsApi.getTagsByDevice(resourceId);
        else if (resourceType === "application") fetchedTags = await tagsApi.getTagsByApp(resourceId);
        else if (resourceType === "profile") fetchedTags = await tagsApi.getTagsByPolicy(resourceId);
        else fetchedTags = [];
      } catch {
        fetchedTags = [];
      }

      setTags(fetchedTags.map((t: any) => ({
        id: t.tag_id || t.tagId || t.id || "", name: t.tag_name || t.tagName || t.name || "",
        color: t.tag_color || t.color || "#6b7280", icon: t.tag_icon || t.icon, type: t.tag_type || t.tagType || t.type || "static",
      })));
    } catch (error) {
      console.error("Error fetching tags:", error);
      setTags([]);
    } finally { setIsLoading(false); }
  };

  const fetchMockTags = () => {
    if (!customerIds || !assignedTagIds) { setTags([]); setIsLoading(false); return; }
    const availableTags = customerIds.flatMap(cid => getTagsByCustomerId(cid));
    const mockTags: Tag[] = availableTags.filter(t => assignedTagIds.includes(t.id)).map(t => ({
      id: t.id, name: t.name, color: t.color || '#6b7280', icon: undefined, type: t.type
    }));
    setTags(mockTags); setIsLoading(false);
  };

  if (isLoading) return <div className={`flex gap-1 ${className}`}><div className="h-5 w-12 bg-muted animate-pulse rounded-full"></div><div className="h-5 w-16 bg-muted animate-pulse rounded-full"></div></div>;

  const handleRemoveTag = (tagId: string, tagName: string, e: React.MouseEvent) => { e.stopPropagation(); setTagToRemove({ id: tagId, name: tagName }); };

  const confirmRemoveTag = async () => {
    if (!tagToRemove) return;
    if (useMockData || (customerIds && assignedTagIds)) {
      setTags(tags.filter(t => t.id !== tagToRemove.id));
      toast({ title: "Tag Removed", description: `"${tagToRemove.name}" has been removed from this ${resourceType}.` });
      onTagRemoved?.(); setTagToRemove(null); return;
    }
    const success = await removeTag(resourceId, tagToRemove.id);
    if (success) { setTags(tags.filter(t => t.id !== tagToRemove.id)); toast({ title: "Tag Removed", description: `"${tagToRemove.name}" has been removed.` }); onTagRemoved?.(); }
    setTagToRemove(null);
  };

  if (tags.length === 0) return null;

  const displayTags = tags.slice(0, maxTags);
  const remainingCount = tags.length - maxTags;

  return (
    <div className={`flex flex-wrap gap-1 ${className}`}>
      {displayTags.map(tag => (
        <Badge key={tag.id} variant="secondary"
          className={`gap-1 group relative ${size === "sm" ? "text-xs px-1.5 py-0.5" : ""} ${showRemoveButton ? "pr-6" : ""}`}
          style={{ backgroundColor: `${tag.color}20`, borderColor: tag.color, color: tag.color }}>
          <TagIcon iconName={tag.icon} color={tag.color} size={size === "sm" ? 6 : 8} />
          {tag.name}
          {showRemoveButton && (
            <button onClick={(e) => handleRemoveTag(tag.id, tag.name, e)} disabled={isRemoving}
              className="absolute right-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/10 rounded-full p-0.5"
              aria-label={`Remove ${tag.name} tag`}>
              <X className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
            </button>
          )}
        </Badge>
      ))}
      {remainingCount > 0 && <Badge variant="outline" className={size === "sm" ? "text-xs px-1.5 py-0.5" : ""}>+{remainingCount}</Badge>}
      
      <AlertDialog open={!!tagToRemove} onOpenChange={(open) => !open && setTagToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-destructive" />Remove Tag?</AlertDialogTitle>
            <AlertDialogDescription>Removing "{tagToRemove?.name}" may trigger configuration changes. Are you sure?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveTag} className="bg-destructive hover:bg-destructive/90">Remove Tag</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
